/**
 * The **only** writer of brand custom properties — ARCHITECTURE §11.5,
 * mechanism 3.
 *
 * "Only" is the load-bearing word and there is a test that enforces it. The
 * palette, the document title, the favicon and the body's visibility are four
 * expressions of one fact — which tenant this page belongs to — and if they are
 * set from four places they will eventually disagree. A page showing Acme's
 * colours under RD's title is worse than either being wrong on its own,
 * because it looks like a data leak.
 *
 * Everything here is synchronous. No awaits, no rAF, no transitions: the whole
 * point is that a repeat visitor's first paint is already correct.
 */

import type { BrandingPayload } from '@rd/shared';

/** Maps a payload colour onto the custom property `globals.css` reads. */
const COLOR_PROPERTIES: [keyof BrandingPayload['colors'], string][] = [
  ['primary', '--brand-primary'],
  ['primaryFg', '--brand-primary-fg'],
  ['accent', '--brand-accent'],
  ['surface', '--brand-surface'],
  ['surfaceAlt', '--brand-surface-alt'],
  ['text', '--brand-text'],
  ['textMuted', '--brand-text-muted'],
];

/**
 * `danger`, `warning` and `success` are in the payload — the API has always
 * returned all ten — but they are **deliberately not applied**. §11.6 makes
 * them platform-defined, and `globals.css` holds them as literals. Writing them
 * here would be the one line that reintroduces a tenant-coloured "Disconnect"
 * button, so the omission is stated rather than left to be noticed.
 */
export const NON_BRANDABLE_COLORS = ['danger', 'warning', 'success'] as const;

const RADIUS_REM: Record<BrandingPayload['radiusScale'], string> = {
  none: '0',
  sm: '0.25rem',
  md: '0.5rem',
  lg: '0.875rem',
};

const FONT_STACK_SUFFIX = 'system-ui, -apple-system, "Segoe UI", sans-serif';

export function applyBranding(payload: BrandingPayload, doc: Document = document): void {
  const root = doc.documentElement;

  for (const [key, property] of COLOR_PROPERTIES) {
    const color = payload.colors[key];
    // The API emits `"15 98 254"`; the space-separated triplet is what lets
    // `rgb(var(--brand-primary) / 70%)` work at all.
    if (color?.rgb) root.style.setProperty(property, color.rgb);
  }

  root.style.setProperty('--brand-radius', RADIUS_REM[payload.radiusScale] ?? RADIUS_REM.md);
  root.style.setProperty(
    '--brand-font',
    // Quoted so a two-word family name survives; the stack is ours, not the
    // tenant's, and the family itself was validated against an allow-list
    // server-side (§11.3) so it can never be a URL.
    `"${payload.fontFamily.replace(/"/g, '')}", ${FONT_STACK_SUFFIX}`,
  );

  doc.title = payload.productName;
  setFavicon(doc, payload.faviconUrl);

  // Last, and unconditionally: whatever else happened above, the page becomes
  // visible. A branding failure must never leave a blank screen (§11.5).
  doc.body.style.visibility = 'visible';
}

function setFavicon(doc: Document, href: string | null): void {
  let link = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = doc.createElement('link');
    link.rel = 'icon';
    doc.head.appendChild(link);
  }
  // `data:,` is the empty placeholder in index.html — a tenant with no favicon
  // gets the browser's default rather than the previous tenant's.
  link.href = href ?? 'data:,';
}

/**
 * The failsafe. If both the cache and the fetch fail, nothing would clear
 * `visibility: hidden` and the user would stare at a blank page — a branding
 * outage would read as a total outage. Called from `main.tsx` on a timer.
 */
export function revealDocument(doc: Document = document): void {
  doc.body.style.visibility = 'visible';
}
